<script lang="ts">
  import uPlot from 'uplot';
  import 'uplot/dist/uPlot.min.css';
  import type { Profile } from '../lib/profile';
  import type { Crossing } from '../lib/airspace';
  import { mToAlt, distScale, type Units } from '../lib/units';

  interface Props {
    profile: Profile;
    crossings: Crossing[];
    units: Units;
    margin: number;
    /** Sample index highlighted from the map, or null. */
    cursor: number | null;
    onscrub: (i: number | null) => void;
  }
  let { profile, crossings, units, margin, cursor, onscrub }: Props = $props();

  let el: HTMLDivElement;
  let plot: uPlot | undefined;
  let fromOutside = false;


  function build() {
    plot?.destroy();
    if (!el) return;
    const s = profile.samples;
    const k = distScale(units);
    const x = s.map((p) => p.d / k);
    const ground = s.map((p) => mToAlt(p.ground, units));
    const marginLine = s.map((p) => mToAlt(p.ground + margin, units));
    const glide = s.map((p) => (Number.isFinite(p.alt) ? mToAlt(p.alt, units) : null));
    // red overlay where clearance is below the margin (include neighbours so segments join)
    const low = s.map((p, i) =>
      Number.isFinite(p.alt) && (p.low || s[i - 1]?.low || s[i + 1]?.low) ? mToAlt(p.alt, units) : null,
    );
    let ymin = Infinity;
    let ymax = -Infinity;
    for (const v of [...ground, ...glide]) {
      if (v === null) continue;
      ymin = Math.min(ymin, v);
      ymax = Math.max(ymax, v);
    }
    const pad = (ymax - ymin) * 0.08 + 20;
    const bands = crossings;
    const opts: uPlot.Options = {
      width: el.clientWidth,
      height: el.clientHeight,
      legend: { show: false },
      cursor: { drag: { x: false, y: false }, points: { size: 8 } },
      scales: { x: { time: false }, y: { range: [ymin - pad, ymax + pad] } },
      axes: [
        {
          stroke: '#475569',
          size: 34,
          values: (_u, v) => v.map((n, i) => (i === v.length - 1 ? `${+n.toFixed(1)} ${units.dist}` : `${+n.toFixed(1)}`)),
          grid: { stroke: '#e2e8f0' },
        },
        {
          stroke: '#475569',
          size: 58,
          values: (_u, v) => v.map((n) => `${Math.round(n).toLocaleString()} ${units.alt}`),
          grid: { stroke: '#e2e8f0' },
        },
      ],
      series: [
        {},
        { label: 'Terrain', stroke: '#78716c', width: 1.5, fill: 'rgba(168,162,158,0.55)' },
        { label: 'Margin', stroke: '#a8a29e', width: 1, dash: [4, 4] },
        { label: 'Glide', stroke: '#1d4ed8', width: 2.5, spanGaps: false },
        { label: 'Low', stroke: '#dc2626', width: 3.5, spanGaps: false },
      ],
      hooks: {
        drawClear: [
          (u) => {
            const ctx = u.ctx;
            ctx.save();
            for (const c of bands) {
              ctx.fillStyle = c.conflict ? 'rgba(225,29,72,0.18)' : 'rgba(124,58,237,0.12)';
              ctx.strokeStyle = c.conflict ? 'rgba(225,29,72,0.7)' : 'rgba(124,58,237,0.5)';
              let i = 0;
              while (i < s.length) {
                if (Number.isNaN(c.floor[i])) {
                  i++;
                  continue;
                }
                let j = i;
                while (j + 1 < s.length && !Number.isNaN(c.floor[j + 1])) j++;
                ctx.beginPath();
                for (let t = i; t <= j; t++) {
                  const px = u.valToPos(x[t], 'x', true);
                  const py = u.valToPos(Math.min(mToAlt(c.ceiling[t], units), ymax + pad), 'y', true);
                  t === i ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
                }
                for (let t = j; t >= i; t--) ctx.lineTo(u.valToPos(x[t], 'x', true), u.valToPos(mToAlt(c.floor[t], units), 'y', true));
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
                i = j + 1;
              }
            }
            ctx.restore();
          },
        ],
        setCursor: [
          (u) => {
            if (fromOutside) return;
            onscrub(u.cursor.idx ?? null);
          },
        ],
      },
    };
    plot = new uPlot(opts, [x, ground, marginLine, glide, low] as uPlot.AlignedData, el);
  }

  $effect(() => {
    void profile;
    void crossings;
    void units;
    void margin;
    build();
    return () => plot?.destroy();
  });

  $effect(() => {
    if (!plot || cursor === null) return;
    const x = profile.samples[cursor]?.d / distScale(units);
    if (x === undefined || Number.isNaN(x)) return;
    fromOutside = true;
    plot.setCursor({ left: plot.valToPos(x, 'x'), top: plot.valToPos(mToAlt(profile.samples[cursor].alt, units), 'y') });
    fromOutside = false;
  });

  $effect(() => {
    const ro = new ResizeObserver(() => plot?.setSize({ width: el.clientWidth, height: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  });
</script>

<div class="chart" bind:this={el} aria-label="Route elevation profile ({units.alt}, distance in {units.dist})" role="img"></div>

<style>
  .chart {
    width: 100%;
    height: 100%;
    min-height: 140px;
  }
</style>
